import { useEffect, useState } from 'react'

import type { Language } from '@/bindings/Language'
import { localeOf } from '@/lib/menu-format'

/** Ticks once a second while `active`, so a countdown re-renders; `Date.now()` otherwise. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [active])
  return now
}

/** Wall-clock time of an ISO instant in the viewer's locale and zone (hours and minutes). */
export function formatTime(iso: string, language: Language): string {
  return new Date(iso).toLocaleTimeString(localeOf(language), {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** When an order or session happened: the time alone if today in the viewer's zone, else date and time. */
export function formatWhen(iso: string, language: Language): string {
  const at = new Date(iso)
  return at.toDateString() === new Date().toDateString() ? formatTime(iso, language) : formatDateTime(iso, language)
}

/** `m:ss` of a remaining span; never negative. */
export function formatCountdown(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** Date and wall-clock time of an ISO instant in the viewer's locale and zone. */
export function formatDateTime(iso: string, language: Language): string {
  return new Date(iso).toLocaleString(localeOf(language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Today as `YYYY-MM-DD` in `timeZone` (a branch's working date, T14); the viewer's zone when none. */
export function todayIn(timeZone: string | undefined): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(timeZone ? { timeZone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** A `YYYY-MM-DD` working date (T14) in the viewer's locale, shown as the date it names in any zone. */
export function formatDate(date: string, language: Language): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(localeOf(language), {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** The month names of the viewer's locale, January first. */
export function monthNames(language: Language): string[] {
  const format = new Intl.DateTimeFormat(localeOf(language), { month: 'long', timeZone: 'UTC' })
  return Array.from({ length: 12 }, (_, month) => format.format(new Date(Date.UTC(2026, month, 1))))
}
